import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import Sidebar from '../components/Sidebar';
import { getActiveAuthRole, getActiveToken } from '../utils/authStorage';
import { getResponseCodeDescriptionForDashboard, getResponseCodeSeverityForDashboard } from './responseCodes';
import {
  FaCalendarAlt,
  FaChartBar,
  FaChartLine,
  FaClock,
  FaCreditCard,
  FaDesktop,
  FaExclamationTriangle,
  FaGlobe,
  FaRedo,
  FaStore,
  FaSyncAlt,
  FaFilter,
  FaBars,
  FaUndo,
} from 'react-icons/fa';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
);

const API_PAYMENT = (process.env.REACT_APP_PAYMENT_SERVICE_URL || 'http://localhost:3000/api').replace(/\/$/, '');
const API_AUTH = (process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:3000/api').replace(/\/$/, '');
const DASH_URL = `${API_PAYMENT}/metrics/dashboard`;

const getToken = () => getActiveToken() || '';

const STORE_CHART_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
const TPE_BAR_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#a78bfa', '#7c3aed', '#4c1d95'];

const DASH = {
  bg: '#070F1E',
  s1: '#0D1B2E',
  s2: '#122040',
  s3: '#1A2D4A',
  border: '#1E3355',
  border2: '#253E62',
  accent: '#00B4D8',
  accent2: '#0EA5E9',
  green: '#10B981',
  red: '#EF4444',
  orange: '#F59E0B',
  purple: '#8B5CF6',
  pink: '#F472B6',
  text: '#E2EAF4',
  muted: '#7A9BC0',
  dim: '#3E5A7A',
};

function buildDashboardQuery(f) {
  const q = new URLSearchParams();
  if (f.from) q.set('from', f.from.includes('T') ? f.from : `${f.from}T00:00:00.000Z`);
  if (f.to) q.set('to', f.to.includes('T') ? f.to : `${f.to}T23:59:59.999Z`);
  if (f.merchantId) q.set('merchantId', String(f.merchantId).trim());
  if (f.storeCode) q.set('storeCode', String(f.storeCode).trim().toUpperCase());
  if (f.tpeId) q.set('tpeId', String(f.tpeId).trim());
  if (f.tagId) q.set('tagId', String(f.tagId).trim());
  if (f.tagIds) q.set('tagIds', String(f.tagIds).trim());
  if (f.stat) q.set('stat', String(f.stat).trim());
  if (f.caisseName) q.set('caisseName', String(f.caisseName).trim());
  return q.toString();
}

/** Query pour `GET .../filters` : sans magasin, TPE ni caisse, pour que les listes déroulantes gardent tous les choix possibles sur la période. */
function buildFiltersMetaQuery(f) {
  return buildDashboardQuery({ ...f, storeCode: '', tpeId: '', caisseName: '' });
}

const EMPTY_FILTERS = {
  from: '',
  to: '',
  merchantId: '',
  storeCode: '',
  caisseName: '',
  tpeId: '',
  stat: '',
  tagId: '',
};

const fmtInt = (v) => (v == null ? '—' : new Intl.NumberFormat('fr-FR').format(v));
const fmtDec = (v, d = 1) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d }).format(v);

/**
 * Même rendu que la colonne « Montant » dans Transation.js :
 * valeur API (millimes) → `Number / 1000`, locale `fr-TN`, 3 décimales, suffixe ` TND`.
 */
const fmtAmountTxnStyle = (v) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(n / 1000)} TND`;
};

/** Valeur déjà exprimée en TND (ex. point d’un graphique après division par 1000) : même style d’affichage, sans re-diviser. */
const fmtTndDisplay = (v) => {
  if (v == null || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `${new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(n)} TND`;
};

function monthlyTableBadge(rate) {
  if (rate >= 92) return 'good';
  if (rate >= 88) return 'ok';
  if (rate >= 75) return 'warn';
  return 'err';
}

function storeRateBadge(rate) {
  if (rate >= 90) return 'good';
  if (rate >= 80) return 'ok';
  if (rate >= 70) return 'warn';
  return 'err';
}

function fmtEvolution(evo) {
  if (evo === null || evo === undefined) return '—';
  return evo >= 0 ? `▲ +${fmtDec(evo)}%` : `▼ ${fmtDec(evo)}%`;
}

function tpeShortLabel(tpeId) {
  const s = String(tpeId ?? '').trim();
  return s || '—';
}

function aggregateCardBrands(brands) {
  let visa = 0;
  let mc = 0;
  let other = 0;
  for (const b of brands || []) {
    const name = String(b.brand || '').toLowerCase();
    if (name.includes('visa')) visa += b.count || 0;
    else if (name.includes('master')) mc += b.count || 0;
    else other += b.count || 0;
  }
  return [
    { label: 'Visa', count: visa, color: DASH.accent2 },
    { label: 'Mastercard', count: mc, color: DASH.orange },
    { label: 'Autre', count: other, color: DASH.dim },
  ];
}

function monthLabelFromKey(key) {
  if (!key || typeof key !== 'string') return key;
  const [y, mo] = key.split('-');
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  const m = parseInt(mo, 10) - 1;
  return m >= 0 && m < 12 ? `${months[m]} ${y}` : key;
}

function severityLabel(sev) {
  if (sev === 'err') return 'Critique';
  if (sev === 'warn') return 'Attention';
  return 'Info';
}

/* ─── UI bits ─────────────────────────────────────────────── */
const Badge = ({ status, children }) => {
  const cls = {
    ok: 'bg-emerald-100 text-emerald-700',
    good: 'bg-green-100 text-green-700',
    warn: 'bg-amber-100 text-amber-700',
    err: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls[status] || cls.info}`}>
      {children}
    </span>
  );
};

const KpiCard = ({ label, value, sub, colorHex }) => (
  <div
    className="bg-white border-slate-200 rounded-xl p-4 border border-l-4 transition-transform hover:-translate-y-0.5"
    style={{ borderLeftColor: colorHex }}
  >
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="my-1 text-2xl font-extrabold" style={{ color: colorHex }}>
      {value}
    </p>
    <p className="text-[10px] text-slate-600">{sub}</p>
  </div>
);

const DonutLegend = ({ items, total }) => {
  const muted = 'text-slate-600';
  return (
    <div className="flex flex-1 flex-col gap-1.5">
      {(items || []).map((item) => {
        const val = item.value ?? item.count ?? 0;
        const label = item.label ?? item.brand ?? '';
        const color = item.color ?? '#64748b';
        return (
          <div key={label} className={`flex items-center gap-2 text-[10px] ${muted}`}>
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
            <span>{label}</span>
            <span className="ml-auto font-bold" style={{ color }}>
              {fmtInt(val)} ({total > 0 ? fmtDec((val / total) * 100) : '0'}%)
            </span>
          </div>
        );
      })}
    </div>
  );
};

const BarRow = ({ label, value, maxValue, color }) => (
  <div className="mb-1.5 flex items-center gap-2">
    <div className="w-28 shrink-0 truncate text-right text-[10px] text-slate-500">{label}</div>
    <div className="h-3.5 flex-1 rounded bg-slate-200">
      <div
        className="h-full rounded transition-all"
        style={{ width: `${maxValue > 0 ? (value / maxValue) * 100 : 0}%`, backgroundColor: color }}
      />
    </div>
    <div className="w-20 shrink-0 text-right text-[10px] text-slate-800">{fmtAmountTxnStyle(value)}</div>
  </div>
);

const TabButton = ({ name, activeTab, onClick, children }) => (
  <button
    type="button"
    onClick={() => onClick(name)}
    className={`px-4 py-1.5 rounded-md text-xs font-semibold border cursor-pointer transition-all duration-200 ${
      activeTab === name
        ? 'bg-sky-600 text-white border-sky-600 shadow-lg shadow-sky-500/25'
        : 'bg-white text-slate-600 border-slate-300 hover:text-slate-900'
    }`}
  >
    {children}
  </button>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-14">
    <div className="h-7 w-7 animate-spin rounded-full border-2 border-t-transparent border-sky-500" />
  </div>
);

const ApiError = ({ msg }) => (
  <div className="rounded-lg border px-4 py-3 text-xs border-red-200 bg-red-50 text-red-700">
    <FaExclamationTriangle className="mb-0.5 mr-1.5 inline h-3 w-3" />
    {msg}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border-slate-200 rounded-xl border p-4 ${className}`}>
    {children}
  </div>
);

const SectionTitle = ({ icon: Icon, children, cls }) => (
  <h2 className={`mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${cls}`}>
    {Icon && <Icon className="h-3 w-3" />}
    {children}
  </h2>
);

/* ─── Dashboard ───────────────────────────────────────────── */
const Dashboard = () => {
  const currentRole = getActiveAuthRole();
  const canFilterByMerchant = currentRole === 'admin' || currentRole === 'finance';
  const [activeTab, setActiveTab] = useState('synthese');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_FILTERS }));

  const [filterOptions, setFilterOptions] = useState({ merchants: [], stores: [], caisses: [], tpes: [], statuses: [] });
  const [tags, setTags] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(null);

  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [weekday, setWeekday] = useState(null);
  const [stores, setStores] = useState(null);
  const [tpes, setTpes] = useState(null);
  const [storesMonthly, setStoresMonthly] = useState(null);
  const [cards, setCards] = useState(null);
  const [incidentsMonthly, setIncidentsMonthly] = useState(null);
  const [errorCodesData, setErrorCodesData] = useState(null);
  const [merchantsLoading, setMerchantsLoading] = useState(false);

  const [loading, setLoading] = useState({});
  const [apiError, setApiError] = useState({});

  const queryString = useMemo(() => buildDashboardQuery(appliedFilters), [appliedFilters]);

  const pageClass = 'bg-slate-100 text-slate-900';
  const titleClass = 'text-slate-900';
  const subtitleClass = 'text-slate-600';
  const accentHighlightClass = 'text-sky-600';
  const sectionTitleCls = 'text-slate-600';
  const tableTextClass = 'text-slate-700';
  const tableHeadClass = 'text-slate-600';
  const borderClass = 'border-slate-200';
  const rowHighlightCls = 'bg-slate-100';
  const filterLabelClass = 'text-slate-500';
  const inputClass = 'border-slate-300 bg-white text-slate-900';
  const toolbarBtnClass = 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50';

  const chartBase = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: '#475569', font: { size: 10 } } },
        tooltip: {
          titleColor: '#0f172a',
          bodyColor: '#0f172a',
        },
      },
      scales: {
        x: {
          ticks: { color: '#475569', font: { size: 9 } },
          grid: { color: '#cbd5e1' },
        },
        y: {
          ticks: { color: '#475569', font: { size: 9 } },
          grid: { color: '#cbd5e1' },
        },
      },
    }),
    [],
  );

  const dashGet = useCallback(async (path) => {
    const qs = queryString;
    const url = `${DASH_URL}/${path}${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (res.status === 401 || res.status === 403) {
      throw new Error('Session expirée ou accès refusé.');
    }
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(t || `HTTP ${res.status}`);
    }
    const data = await res.json();
    if (data && data.success === false) throw new Error(data.error || 'Réponse API invalide');
    return data;
  }, [queryString]);

  const fetchMerchants = useCallback(async () => {
    if (!canFilterByMerchant) return [];
    setMerchantsLoading(true);
    try {
      const res = await fetch(`${API_AUTH}/merchants`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Marchands HTTP ${res.status}`);
      return Array.isArray(data.merchants) ? data.merchants : (Array.isArray(data) ? data : []);
    } finally {
      setMerchantsLoading(false);
    }
  }, [canFilterByMerchant]);

  /* Init: filters + tags */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFiltersLoading(true);
      setFiltersError(null);
      try {
        const qs = buildFiltersMetaQuery(appliedFilters);
        const [fRes, tRes, merchantsList] = await Promise.all([
          fetch(`${DASH_URL}/filters${qs ? `?${qs}` : ''}`, { headers: { Authorization: `Bearer ${getToken()}` } }),
          fetch(`${API_AUTH}/tags`, { headers: { Authorization: `Bearer ${getToken()}` } }),
          fetchMerchants(),
        ]);
        const fJson = await fRes.json().catch(() => ({}));
        const tJson = await tRes.json().catch(() => ({}));
        if (!fRes.ok) throw new Error(fJson?.error || `Filtres HTTP ${fRes.status}`);
        if (cancelled) return;
        setFilterOptions({
          merchants: Array.isArray(merchantsList) ? merchantsList : [],
          stores: Array.isArray(fJson.stores) ? fJson.stores : [],
          caisses: Array.isArray(fJson.caisses) ? fJson.caisses : [],
          tpes: Array.isArray(fJson.tpes) ? fJson.tpes : [],
          statuses: Array.isArray(fJson.statuses) ? fJson.statuses : [],
        });
        setTags(Array.isArray(tJson.tags) ? tJson.tags : []);
      } catch (e) {
        if (!cancelled) setFiltersError(e.message || 'Erreur chargement filtres');
      } finally {
        if (!cancelled) setFiltersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, queryString, appliedFilters, fetchMerchants]);

  const loadTab = useCallback(
    async (tab) => {
      const setL = (k, v) => setLoading((p) => ({ ...p, [k]: v }));
      const setE = (k, v) => setApiError((p) => ({ ...p, [k]: v }));

      const run = async (key, fn) => {
        setL(key, true);
        setE(key, null);
        try {
          await fn();
        } catch (e) {
          setE(key, e.message || 'Erreur');
        } finally {
          setL(key, false);
        }
      };

      if (tab === 'synthese') {
        await Promise.all([
          run('syn_summary', async () => setSummary(await dashGet('summary'))),
          run('syn_monthly', async () => setMonthly(await dashGet('monthly'))),
          run('syn_cards', async () => setCards(await dashGet('cards'))),
        ]);
      } else if (tab === 'tendances') {
        await Promise.all([
          run('ten_monthly', async () => setMonthly(await dashGet('monthly'))),
          run('ten_hourly', async () => setHourly(await dashGet('hourly'))),
          run('ten_weekday', async () => setWeekday(await dashGet('weekday'))),
        ]);
      } else if (tab === 'tpe') {
        await Promise.all([
          run('tpe_stores', async () => setStores(await dashGet('stores'))),
          run('tpe_tpes', async () => setTpes(await dashGet('tpes'))),
          run('tpe_sm', async () => setStoresMonthly(await dashGet('stores-monthly'))),
        ]);
      } else if (tab === 'incidents') {
        await Promise.all([
          run('inc_summary', async () => setSummary(await dashGet('summary'))),
          run('inc_monthly', async () => setMonthly(await dashGet('monthly'))),
          run('inc_im', async () => setIncidentsMonthly(await dashGet('incidents-monthly'))),
          run('inc_ec', async () => setErrorCodesData(await dashGet('error-codes'))),
        ]);
      }
    },
    [dashGet],
  );

  useEffect(() => {
    loadTab(activeTab);
  }, [activeTab, loadTab, refreshKey, queryString]);

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters });
  };

  const resetFilters = () => {
    const cleared = { ...EMPTY_FILTERS };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
  };

  const handleRefresh = () => {
    setSummary(null);
    setMonthly(null);
    setHourly(null);
    setWeekday(null);
    setStores(null);
    setTpes(null);
    setStoresMonthly(null);
    setCards(null);
    setIncidentsMonthly(null);
    setErrorCodesData(null);
    setLoading({});
    setApiError({});
    setRefreshKey((k) => k + 1);
  };

  const monthlyData = monthly?.monthly ?? [];
  const lastIdx = monthlyData.length - 1;
  const monthLabels = useMemo(() => monthlyData.map((m) => m.label), [monthlyData]);
  const monthAppr = useMemo(() => monthlyData.map((m) => m.approved), [monthlyData]);
  const monthFailed = useMemo(() => monthlyData.map((m) => m.total - m.approved), [monthlyData]);
  const monthRates = useMemo(() => monthlyData.map((m) => m.successRate), [monthlyData]);
  const monthVolumes = useMemo(
    () => monthlyData.map((m) => (Number.isFinite(Number(m.approvedVolume)) ? Number(m.approvedVolume) / 1000 : 0)),
    [monthlyData],
  );
  const monthAvgBasket = useMemo(
    () => monthlyData.map((m) => (Number.isFinite(Number(m.avgBasket)) ? Number(m.avgBasket) / 1000 : 0)),
    [monthlyData],
  );
  const monthApprColors = useMemo(
    () => monthlyData.map((_, i) => (i === lastIdx ? DASH.accent2 : DASH.green)),
    [monthlyData, lastIdx],
  );
  const ratePointColors = useMemo(
    () => monthRates.map((r) => (r >= 90 ? DASH.green : r >= 80 ? DASH.orange : DASH.red)),
    [monthRates],
  );

  const hourlyBuckets = hourly?.hourlyDistribution ?? [];
  const weekdayBuckets = weekday?.weekdayDistribution ?? [];
  const weekdayMax = useMemo(() => Math.max(0, ...weekdayBuckets.map((w) => w.count)), [weekdayBuckets]);

  const tpeList = tpes?.tpes ?? [];
  const storeList = stores?.stores ?? [];
  const brandList = cards?.brands ?? [];
  const errCodeList = errorCodesData?.errorCodes ?? [];
  const incidentsSeries = incidentsMonthly?.series ?? [];

  const tpeMaxVol = useMemo(() => Math.max(...tpeList.map((t) => t.approvedVolume), 1), [tpeList]);
  const brandTotal = useMemo(() => brandList.reduce((s, b) => s + (b.count || 0), 0), [brandList]);
  const statusTotal = summary?.totalTransactions ?? 0;
  const failRate = summary && summary.totalTransactions
    ? Math.max(0, 100 - (summary.successRate || 0))
    : 0;

  const statusItems = useMemo(
    () => (summary?.statusBreakdown ?? []).map((s) => ({ ...s, value: s.count })),
    [summary],
  );

  const cardAgg = useMemo(() => aggregateCardBrands(brandList), [brandList]);
  const cardAggTotal = useMemo(() => cardAgg.reduce((s, c) => s + c.count, 0), [cardAgg]);

  const storesMonthlyChart = useMemo(() => {
    const sm = storesMonthly;
    if (!sm || !Array.isArray(sm.months) || !Array.isArray(sm.stores)) return null;
    const months = sm.months;
    const labels = months.map(monthLabelFromKey);
    const datasets = [];
    sm.stores.forEach((st, idx) => {
      const color = STORE_CHART_COLORS[idx % STORE_CHART_COLORS.length];
      const byMonth = new Map((st.monthly || []).map((row) => [row.month, row]));
      const appr = months.map((m) => byMonth.get(m)?.approved ?? 0);
      const fail = months.map((m) => byMonth.get(m)?.failed ?? 0);
      const code = st.storeCode || '—';
      datasets.push({
        type: 'bar',
        label: `${code} OK`,
        data: appr,
        stack: code,
        backgroundColor: color,
        borderRadius: 2,
      });
      datasets.push({
        type: 'bar',
        label: `${code} Échecs`,
        data: fail,
        stack: code,
        backgroundColor: `${color}99`,
        borderRadius: 2,
      });
    });
    const lineDatasets = sm.stores.map((st, idx) => {
      const byMonth = new Map((st.monthly || []).map((row) => [row.month, row]));
      const vols = months.map((m) => {
        const raw = byMonth.get(m)?.approvedVolume;
        return Number.isFinite(Number(raw)) ? Number(raw) / 1000 : 0;
      });
      const color = STORE_CHART_COLORS[idx % STORE_CHART_COLORS.length];
      return {
        type: 'line',
        label: `Vol. ${st.storeCode || '—'}`,
        data: vols,
        borderColor: color,
        backgroundColor: 'transparent',
        tension: 0.35,
        yAxisID: 'y1',
        pointRadius: 3,
      };
    });
    return { labels, barDatasets: datasets, lineDatasets, months };
  }, [storesMonthly]);

  const incidentsStackData = useMemo(() => {
    const labels = incidentsSeries.map((r) => r.label || monthLabelFromKey(r.month));
    return {
      labels,
      datasets: [
        { label: 'TPE indispo.', data: incidentsSeries.map((r) => r.TU ?? 0), backgroundColor: DASH.purple, stack: 'i' },
        { label: 'Timeout', data: incidentsSeries.map((r) => r.TO ?? 0), backgroundColor: DASH.orange, stack: 'i' },
        { label: 'Annulé', data: incidentsSeries.map((r) => r.CA ?? 0), backgroundColor: DASH.dim, stack: 'i' },
        { label: 'Rejeté', data: incidentsSeries.map((r) => r.RJ ?? 0), backgroundColor: DASH.red, stack: 'i' },
      ],
    };
  }, [incidentsSeries]);

  /** Totaux TU / TO / CA / RJ sur la période (donut « qualité » incidents). */
  const incidentsTypeDonutItems = useMemo(() => {
    let tu = 0;
    let to = 0;
    let ca = 0;
    let rj = 0;
    for (const r of incidentsSeries) {
      tu += r.TU ?? 0;
      to += r.TO ?? 0;
      ca += r.CA ?? 0;
      rj += r.RJ ?? 0;
    }
    return [
      { label: 'TPE indispo. (TU)', count: tu, color: DASH.purple },
      { label: 'Timeout (TO)', count: to, color: DASH.orange },
      { label: 'Annulé (CA)', count: ca, color: DASH.dim },
      { label: 'Rejeté (RJ)', count: rj, color: DASH.red },
    ];
  }, [incidentsSeries]);

  /** Transactions non approuvées (dénominateur des % « des incidents » comme sur la maquette). */
  const totalNonApprovedIncidents = useMemo(
    () => Math.max(0, (summary?.totalTransactions ?? 0) - (summary?.approved ?? 0)),
    [summary],
  );

  /** Montant TND non encaissé : champs API optionnels, sinon estimation Σ (échecs mois × panier moyen). */
  const uncollectedTndInfo = useMemo(() => {
    const s = summary;
    if (s && typeof s === 'object') {
      const keys = ['unapprovedVolume', 'failedVolume', 'nonApprovedVolume', 'declinedVolume', 'uncollectedAmount'];
      for (const k of keys) {
        const v = s[k];
        if (v != null && v !== '' && !Number.isNaN(Number(v))) return { amount: Number(v), isEstimate: false };
      }
      if (s.totalVolume != null && s.approvedVolume != null) {
        const x = Number(s.totalVolume) - Number(s.approvedVolume);
        if (!Number.isNaN(x) && x >= 0) return { amount: x, isEstimate: false };
      }
    }
    let est = 0;
    for (const m of monthlyData) {
      const failed = Math.max(0, (m.total || 0) - (m.approved || 0));
      est += failed * (Number(m.avgBasket) || 0);
    }
    if (est > 0) return { amount: est, isEstimate: true };
    return { amount: null, isEstimate: false };
  }, [summary, monthlyData]);

  const incFluxTuToCaRj = useMemo(() => {
    const it = incidentsTypeDonutItems;
    return {
      tu: it[0]?.count ?? 0,
      to: it[1]?.count ?? 0,
      ca: it[2]?.count ?? 0,
      rj: it[3]?.count ?? 0,
    };
  }, [incidentsTypeDonutItems]);

  const monthlyFailRates = useMemo(
    () => monthlyData.map((m) => Math.max(0, 100 - (Number(m.successRate) || 0))),
    [monthlyData],
  );

  const topErrorCodesForChart = useMemo(() => {
    const list = [...(errCodeList || [])].sort((a, b) => (b.count || 0) - (a.count || 0));
    return list.slice(0, 12);
  }, [errCodeList]);

  const topErrorBarColors = useMemo(
    () =>
      topErrorCodesForChart.map((item) => {
        const sev = getResponseCodeSeverityForDashboard(item.code);
        if (sev === 'err') return DASH.red;
        if (sev === 'warn') return DASH.orange;
        return DASH.accent2;
      }),
    [topErrorCodesForChart],
  );

  const tabErrored =
    (activeTab === 'synthese' && (apiError.syn_summary || apiError.syn_monthly || apiError.syn_cards)) ||
    (activeTab === 'tendances' && (apiError.ten_monthly || apiError.ten_hourly || apiError.ten_weekday)) ||
    (activeTab === 'tpe' && (apiError.tpe_stores || apiError.tpe_tpes || apiError.tpe_sm)) ||
    (activeTab === 'incidents' &&
      (apiError.inc_summary || apiError.inc_monthly || apiError.inc_im || apiError.inc_ec));

  const tabLoading =
    (activeTab === 'synthese' && (loading.syn_summary || loading.syn_monthly || loading.syn_cards)) ||
    (activeTab === 'tendances' && (loading.ten_monthly || loading.ten_hourly || loading.ten_weekday)) ||
    (activeTab === 'tpe' && (loading.tpe_stores || loading.tpe_tpes || loading.tpe_sm)) ||
    (activeTab === 'incidents' && (loading.inc_summary || loading.inc_monthly || loading.inc_im || loading.inc_ec));

  return (
    <div className={`relative min-h-screen font-dash-sans ${pageClass}`}>
      <div className="flex min-h-screen">
        <aside className="hidden md:block h-screen sticky top-0 shrink-0">
          <Sidebar />
        </aside>

        {mobileSidebarOpen && (
          <div className="md:hidden fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-black/50"
              aria-label="Fermer le menu"
              onClick={() => setMobileSidebarOpen(false)}
            />
            <div className="relative z-10 h-screen w-56">
              <Sidebar />
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 p-4 pb-10">
          <header className="mb-4 flex items-start gap-3 sm:items-center">
            <button
              type="button"
              className={`md:hidden shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold ${toolbarBtnClass}`}
              onClick={() => setMobileSidebarOpen(true)}
            >
              <FaBars className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className={`text-lg font-bold leading-tight ${titleClass}`}>Tableau de bord ECRLINK</h1>
              <p className={`mt-0.5 text-xs ${subtitleClass}`}>
                Indicateurs agrégés (fuseau Africa/Tunis) ·{' '}
                {summary != null ? (
                  <>
                    <span className={`font-semibold ${accentHighlightClass}`}>
                      {fmtInt(summary.totalTransactions)}
                    </span>{' '}
                    transactions sur la période
                  </>
                ) : (
                  'Chargement…'
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              className={`inline-flex shrink-0 items-center gap-1.5 self-center rounded-md border px-3 py-1.5 text-xs font-semibold transition-all ${toolbarBtnClass}`}
            >
              <FaRedo className="h-3 w-3" /> Actualiser
            </button>
          </header>

          {/* Filtres */}
          <Card className="mb-4">
            <SectionTitle icon={FaFilter} cls={sectionTitleCls}>
              Filtres
            </SectionTitle>
            {filtersError && <ApiError msg={filtersError} />}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 items-end">
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                Date début
                <input
                  type="date"
                  value={draftFilters.from}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, from: e.target.value }))}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                />
              </label>
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                Date fin
                <input
                  type="date"
                  value={draftFilters.to}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, to: e.target.value }))}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                />
              </label>
              {canFilterByMerchant && (
                <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                  Marchand
                  <select
                    value={draftFilters.merchantId}
                    onChange={(e) =>
                      setDraftFilters((f) => ({
                        ...f,
                        merchantId: e.target.value,
                        storeCode: '',
                        caisseName: '',
                      }))
                    }
                    disabled={filtersLoading || merchantsLoading}
                    className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                  >
                    <option value="">Tous</option>
                    {filterOptions.merchants.map((m) => {
                      const label =
                        m.userFullName ||
                        `${m?.user?.name || m?.userName || ''} ${m?.user?.lastName || m?.userLastName || ''}`.trim() ||
                        m.email ||
                        m.id;
                      return (
                        <option key={m.id} value={m.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                Magasin
                <select
                  value={draftFilters.storeCode}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, storeCode: e.target.value }))}
                  disabled={filtersLoading}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                >
                  <option value="">Tous</option>
                  {filterOptions.stores
                    .filter((s) => {
                      if (!draftFilters.merchantId) return true;
                      const merchantId = s?.merchantId && typeof s.merchantId === 'object'
                        ? (s.merchantId.id || s.merchantId._id || '')
                        : (s?.merchantId || '');
                      return String(merchantId || '') === String(draftFilters.merchantId);
                    })
                    .map((s) => (
                    <option key={s.storeCode} value={s.storeCode}>
                      {s.storeCode}
                    </option>
                  ))}
                </select>
              </label>
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                Caisse
                <select
                  value={draftFilters.caisseName}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, caisseName: e.target.value }))}
                  disabled={filtersLoading}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                >
                  <option value="">Toutes</option>
                  {[...new Set(filterOptions.caisses.map((c) => c.caisseName).filter(Boolean))].map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                TPE
                <select
                  value={draftFilters.tpeId}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, tpeId: e.target.value }))}
                  disabled={filtersLoading}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                >
                  <option value="">Tous</option>
                  {filterOptions.tpes.map((t) => (
                    <option key={t.tpeId} value={t.tpeId}>
                      {tpeShortLabel(t.tpeId)}
                    </option>
                  ))}
                </select>
              </label>
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                Statut
                <select
                  value={draftFilters.stat}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, stat: e.target.value }))}
                  disabled={filtersLoading}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                >
                  <option value="">Tous</option>
                  {filterOptions.statuses.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={`block text-[10px] uppercase font-semibold ${filterLabelClass}`}>
                Tag TPE
                <select
                  value={draftFilters.tagId}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, tagId: e.target.value }))}
                  disabled={filtersLoading}
                  className={`mt-1 w-full rounded-md border px-2 py-1.5 text-xs ${inputClass}`}
                >
                  <option value="">Tous</option>
                  {tags.map((tg) => (
                    <option key={tg._id || tg.id} value={tg._id || tg.id}>
                      {tg.name || tg.slug || tg._id}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={applyFilters}
                className="rounded-md bg-dash-accent px-4 py-2 text-xs font-semibold text-white hover:brightness-110"
              >
                Appliquer les filtres
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className={`inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-xs font-semibold ${toolbarBtnClass}`}
              >
                <FaUndo className="h-3 w-3" />
                Réinitialiser
              </button>

            </div>
          </Card>

          <div className="flex flex-wrap gap-1 mb-4">
            <TabButton name="synthese" activeTab={activeTab} onClick={setActiveTab}>
              <span className="inline-flex items-center gap-1.5">
                <FaGlobe className="h-3 w-3" />
                Synthèse
              </span>
            </TabButton>
            <TabButton name="tendances" activeTab={activeTab} onClick={setActiveTab}>
              <span className="inline-flex items-center gap-1.5">
                <FaChartLine className="h-3 w-3" />
                Tendances
              </span>
            </TabButton>
            <TabButton name="tpe" activeTab={activeTab} onClick={setActiveTab}>
              <span className="inline-flex items-center gap-1.5">
                <FaDesktop className="h-3 w-3" />
                TPE &amp; Magasins
              </span>
            </TabButton>
            <TabButton name="incidents" activeTab={activeTab} onClick={setActiveTab}>
              <span className="inline-flex items-center gap-1.5">
                <FaExclamationTriangle className="h-3 w-3" />
                Incidents &amp; Qualité
              </span>
            </TabButton>
          </div>

          {tabErrored && !tabLoading && (
            <div className="mb-3 space-y-2">
              {Object.entries(apiError)
                .filter(([k, v]) => {
                  if (!v) return false;
                  if (activeTab === 'synthese') return k.startsWith('syn_');
                  if (activeTab === 'tendances') return k.startsWith('ten_');
                  if (activeTab === 'tpe') return k.startsWith('tpe_');
                  if (activeTab === 'incidents') return k.startsWith('inc_');
                  return false;
                })
                .map(([k, v]) => (
                  <ApiError key={k} msg={v} />
                ))}
            </div>
          )}

          {tabLoading && <Spinner />}

          {/* ─── Synthèse ─── */}
          {activeTab === 'synthese' && !tabLoading && !tabErrored && summary && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <KpiCard
                  label="Total transactions"
                  value={fmtInt(summary.totalTransactions)}
                  sub="Période filtrée"
                  colorHex={DASH.accent2}
/>
                <KpiCard
                  label="Approuvées"
                  value={fmtInt(summary.approved)}
                  sub={`${fmtDec(summary.successRate)} % taux de succès`}
                  colorHex={DASH.green}
/>
                <KpiCard
                  label="Volume approuvé"
                  value={fmtAmountTxnStyle(summary.approvedVolume)}
                  sub={`${fmtInt(summary.approved)} transactions approuvées`}
                  colorHex={DASH.accent}
/>
                <KpiCard
                  label="Panier moyen"
                  value={fmtAmountTxnStyle(summary.avgBasket)}
                  sub="Sur transactions approuvées"
                  colorHex={DASH.pink}
/>
                <KpiCard
                  label="Incidents"
                  value={fmtInt((summary.totalTransactions || 0) - (summary.approved || 0))}
                  sub={`${fmtDec(failRate)} % taux d'échec`}
                  colorHex={DASH.red}
/>
                <KpiCard
                  label="Mois en cours"
                  value={fmtAmountTxnStyle(summary.currentMonth?.volume)}
                  sub={`${summary.currentMonth?.label ?? '—'} · ${fmtDec(summary.currentMonth?.successRate)} % succès`}
                  colorHex={DASH.orange}
/>
              </div>

              <div className="grid gap-3 lg:grid-cols-3">
                <Card className="lg:col-span-2">
                  <SectionTitle icon={FaCalendarAlt} cls={sectionTitleCls}>
                    Transactions par mois (empilé)
                  </SectionTitle>
                  <div className="h-64">
                    <Bar
                      data={{
                        labels: monthLabels,
                        datasets: [
                          { label: 'Approuvées', data: monthAppr, backgroundColor: monthApprColors, borderRadius: 4 },
                          { label: 'Non approuvées', data: monthFailed, backgroundColor: DASH.red, borderRadius: 4 },
                        ],
                      }}
                      options={{
                        ...chartBase,
                        scales: {
                          ...chartBase.scales,
                          x: { ...chartBase.scales.x, stacked: true },
                          y: { ...chartBase.scales.y, stacked: true },
                        },
                      }}
                    />
                  </div>
                </Card>
                <Card>
                  <SectionTitle icon={FaSyncAlt} cls={sectionTitleCls}>
                    Répartition des statuts
                  </SectionTitle>
                  <div className="flex items-center gap-3">
                    <div className="h-36 w-36 shrink-0">
                      <Doughnut
                        data={{
                          labels: statusItems.map((s) => s.label),
                          datasets: [
                            {
                              data: statusItems.map((s) => s.count),
                              backgroundColor: statusItems.map((s) => s.color),
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          cutout: '62%',
                          plugins: { legend: { display: false } },
                        }}
                      />
                    </div>
                    <DonutLegend items={statusItems} total={statusTotal} />
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-stretch">
                <Card className="flex min-h-0 flex-col">
                  <SectionTitle icon={FaChartLine} cls={sectionTitleCls}>
                    Taux de succès mensuel
                  </SectionTitle>
                  <div className="h-56 min-h-[14rem] flex-1">
                    <Line
                      data={{
                        labels: monthLabels,
                        datasets: [
                          {
                            label: 'Taux (%)',
                            data: monthRates,
                            borderColor: DASH.accent2,
                            backgroundColor: 'rgba(14, 165, 233, 0.12)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: 5,
                            pointBackgroundColor: ratePointColors,
                            pointBorderColor: '#fff',
                            pointBorderWidth: 2,
                          },
                        ],
                      }}
                      options={{
                        ...chartBase,
                        plugins: { ...chartBase.plugins, legend: { display: false } },
                        scales: { ...chartBase.scales, y: { ...chartBase.scales.y, min: 0, max: 100 } },
                      }}
                    />
                  </div>
                </Card>

                <Card className="flex min-h-0 flex-col">
                  <SectionTitle icon={FaCreditCard} cls={sectionTitleCls}>
                    Réseaux de carte (Visa / Mastercard / Autre)
                  </SectionTitle>
                  {cardAggTotal === 0 ? (
                    <p className={`text-xs ${subtitleClass}`}>Aucune donnée carte sur cette période.</p>
                  ) : (
                    <div className="h-56 min-h-[14rem] flex-1">
                      <Bar
                        data={{
                          labels: cardAgg.map((c) => c.label),
                          datasets: [
                            {
                              label: 'Transactions',
                              data: cardAgg.map((c) => c.count),
                              backgroundColor: cardAgg.map((c) => c.color),
                              borderRadius: 6,
                            },
                          ],
                        }}
                        options={{
                          indexAxis: 'y',
                          ...chartBase,
                          plugins: { ...chartBase.plugins, legend: { display: false } },
                        }}
                      />
                    </div>
                  )}
                </Card>
              </div>
            </div>
          )}

          {/* ─── Tendances ─── */}
          {activeTab === 'tendances' && !tabLoading && !tabErrored && (
            <div className="space-y-3">
              <div className="grid gap-3 lg:grid-cols-2">
                <Card>
                  <SectionTitle icon={FaChartBar} cls={sectionTitleCls}>
                    Volume approuvé + taux de succès
                  </SectionTitle>
                  <div className="h-64">
                    <Bar
                      data={{
                        labels: monthLabels,
                        datasets: [
                          {
                            type: 'bar',
                            label: 'Volume (TND)',
                            data: monthVolumes,
                            yAxisID: 'y',
                            backgroundColor: 'rgba(99,102,241,0.55)',
                            borderRadius: 4,
                          },
                          {
                            type: 'line',
                            label: 'Taux %',
                            data: monthRates,
                            yAxisID: 'y1',
                            borderColor: '#f59e0b',
                            backgroundColor: 'transparent',
                            tension: 0.35,
                            pointBackgroundColor: '#f59e0b',
                          },
                        ],
                      }}
                      options={{
                        ...chartBase,
                        plugins: {
                          ...chartBase.plugins,
                          tooltip: {
                            ...chartBase.plugins.tooltip,
                            callbacks: {
                              label(ctx) {
                                const dsLabel = ctx.dataset?.label ?? '';
                                if (dsLabel === 'Taux %') {
                                  const y = ctx.parsed.y;
                                  return `${dsLabel}: ${Number.isFinite(y) ? `${fmtDec(y)} %` : '-'}`;
                                }
                                if (dsLabel === 'Volume (TND)') {
                                  const y = ctx.parsed.y;
                                  return `${dsLabel}: ${fmtTndDisplay(y)}`;
                                }
                                return `${dsLabel}: ${ctx.formattedValue ?? ''}`;
                              },
                            },
                          },
                        },
                        scales: {
                          ...chartBase.scales,
                          y: { ...chartBase.scales.y, position: 'left' },
                          y1: {
                            type: 'linear',
                            position: 'right',
                            min: 0,
                            max: 100,
                            ticks: { color: DASH.orange, font: { size: 9 } },
                            grid: { display: false },
                          },
                        },
                      }}
                    />
                  </div>
                </Card>
                <Card>
                  <SectionTitle icon={FaChartLine} cls={sectionTitleCls}>
                    Panier moyen mensuel
                  </SectionTitle>
                  <div className="h-64">
                    <Line
                      data={{
                        labels: monthLabels,
                        datasets: [
                          {
                            label: 'Panier moyen (TND)',
                            data: monthAvgBasket,
                            borderColor: '#10b981',
                            backgroundColor: 'rgba(16,185,129,0.12)',
                            fill: true,
                            tension: 0.35,
                          },
                        ],
                      }}
                      options={{
                        ...chartBase,
                        plugins: {
                          ...chartBase.plugins,
                          tooltip: {
                            ...chartBase.plugins.tooltip,
                            callbacks: {
                              label(ctx) {
                                return `${ctx.dataset.label}: ${fmtTndDisplay(ctx.parsed.y)}`;
                              },
                            },
                          },
                          legend: { display: false },
                        },
                      }}
                    />
                  </div>
                </Card>
              </div>

              <div className="grid gap-3 lg:grid-cols-2">
                <Card>
                  <SectionTitle icon={FaClock} cls={sectionTitleCls}>
                    Activité par heure (période filtrée)
                  </SectionTitle>
                  <div className="h-64">
                    <Bar
                      data={{
                        labels: hourlyBuckets.map((h) => `${h.hour}h`),
                        datasets: [
                          {
                            data: hourlyBuckets.map((h) => h.count),
                            borderRadius: 3,
                            backgroundColor: hourlyBuckets.map((h) =>
                              h.count >= 400 ? '#0EA5E9' : h.count >= 300 ? '#3B82F6' : '#1D4ED8',
                            ),
                          },
                        ],
                      }}
                      options={{ ...chartBase, plugins: { ...chartBase.plugins, legend: { display: false } } }}
                    />
                  </div>
                </Card>
                <Card>
                  <SectionTitle icon={FaCalendarAlt} cls={sectionTitleCls}>
                    Répartition par jour de la semaine
                  </SectionTitle>
                  <div className="h-64">
                    <Bar
                      data={{
                        labels: weekdayBuckets.map((w) => w.label),
                        datasets: [
                          {
                            data: weekdayBuckets.map((w) => w.count),
                            backgroundColor: weekdayBuckets.map((w) =>
                              w.count > 0 && w.count === weekdayMax ? '#00B4D8' : '#0ea5e9',
                            ),
                            borderRadius: 4,
                          },
                        ],
                      }}
                      options={{ ...chartBase, plugins: { ...chartBase.plugins, legend: { display: false } } }}
                    />
                  </div>
                </Card>
              </div>

              <Card>
                <SectionTitle icon={FaChartBar} cls={sectionTitleCls}>
                  Détail mensuel
                </SectionTitle>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={`${tableHeadClass} font-semibold text-left`}>
                        <th className="py-1.5 px-2">Mois</th>
                        <th className="py-1.5 px-2">Total</th>
                        <th className="py-1.5 px-2">Approuvées</th>
                        <th className="py-1.5 px-2">Taux</th>
                        <th className="py-1.5 px-2">Volume</th>
                        <th className="py-1.5 px-2">Panier</th>
                        <th className="py-1.5 px-2">Évolution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.map((m, i) => (
                        <tr
                          key={m.month}
                          className={`border-b ${borderClass} ${i === lastIdx ? `${rowHighlightCls} font-bold` : ''}`}
                        >
                          <td className={`py-1.5 px-2 ${tableTextClass}`}>{m.label}</td>
                          <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtInt(m.total)}</td>
                          <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtInt(m.approved)}</td>
                          <td className="py-1.5 px-2">
                            <Badge status={monthlyTableBadge(m.successRate)}>
                              {fmtDec(m.successRate)} %
                            </Badge>
                          </td>
                          <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtAmountTxnStyle(m.approvedVolume)}</td>
                          <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtAmountTxnStyle(m.avgBasket)}</td>
                          <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtEvolution(m.evolution)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* ─── TPE & Magasins ─── */}
          {activeTab === 'tpe' && !tabLoading && !tabErrored && (
            <div className="space-y-3">
              <div className="grid gap-3 lg:grid-cols-2">
                <Card>
                  <SectionTitle icon={FaStore} cls={sectionTitleCls}>
                    Performance par magasin
                  </SectionTitle>
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={`${tableHeadClass} font-semibold text-left`}>
                          <th className="py-1.5 px-2">Magasin</th>
                          <th className="py-1.5 px-2">Total</th>
                          <th className="py-1.5 px-2">Appro.</th>
                          <th className="py-1.5 px-2">Taux</th>
                          <th className="py-1.5 px-2">Volume TND</th>
                          <th className="py-1.5 px-2">Panier</th>
                        </tr>
                      </thead>
                      <tbody>
                        {storeList.map((s, i) => (
                          <tr key={s.storeCode ?? i} className={`border-b ${borderClass}`}>
                            <td className={`py-1.5 px-2 font-bold ${tableTextClass}`}>{s.storeCode}</td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtInt(s.total)}</td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtInt(s.approved)}</td>
                            <td className="py-1.5 px-2">
                              <Badge status={storeRateBadge(s.successRate)}>
                                {fmtDec(s.successRate)} %
                              </Badge>
                            </td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtAmountTxnStyle(s.approvedVolume)}</td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtAmountTxnStyle(s.avgBasket)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {storeList.length > 0 && (
                    <div className="h-48 mt-3">
                      <Doughnut
                        data={{
                          labels: storeList.map((s) => `Mag. ${s.storeCode}`),
                          datasets: [
                            {
                              data: storeList.map((s) => s.total),
                              backgroundColor: storeList.map((_, i) => STORE_CHART_COLORS[i % STORE_CHART_COLORS.length]),
                              borderWidth: 0,
                            },
                          ],
                        }}
                        options={{ ...chartBase, cutout: '55%' }}
                      />
                    </div>
                  )}
                </Card>
                <Card>
                  <SectionTitle icon={FaDesktop} cls={sectionTitleCls}>
                    Performance par TPE
                  </SectionTitle>
                  <div className="overflow-x-auto max-h-80">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={`${tableHeadClass} font-semibold text-left`}>
                          <th className="py-1.5 px-2">Terminal</th>
                          <th className="py-1.5 px-2">Total</th>
                          <th className="py-1.5 px-2">Taux</th>
                          <th className="py-1.5 px-2">Volume TND</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tpeList.map((t, i) => (
                          <tr key={t.tpeId ?? i} className={`border-b ${borderClass}`}>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{tpeShortLabel(t.tpeId)}</td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtInt(t.total)}</td>
                            <td className="py-1.5 px-2">
                              <Badge status={t.successRate >= 85 ? 'ok' : 'warn'}>
                                {fmtDec(t.successRate)} %
                              </Badge>
                            </td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtAmountTxnStyle(t.approvedVolume)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>

              <Card>
                <SectionTitle icon={FaDesktop} cls={sectionTitleCls}>
                  Volume approuvé par TPE
                </SectionTitle>
                {tpeList.map((t, i) => (
                  <BarRow
                    key={t.tpeId ?? i}
                    label={`${tpeShortLabel(t.tpeId)} (${fmtDec(t.successRate)}%)`}
                    value={t.approvedVolume}
                    maxValue={tpeMaxVol}
                    color={TPE_BAR_COLORS[i % TPE_BAR_COLORS.length]}
/>
                ))}
              </Card>

              {storesMonthlyChart &&
                (storesMonthlyChart.barDatasets.length > 0 || storesMonthlyChart.lineDatasets.length > 0) && (
                  <div
                    className={`grid gap-3 items-stretch ${
                      storesMonthlyChart.barDatasets.length > 0 && storesMonthlyChart.lineDatasets.length > 0
                        ? 'grid-cols-1 lg:grid-cols-2'
                        : 'grid-cols-1'
                    }`}
                  >
                    {storesMonthlyChart.barDatasets.length > 0 && (
                      <Card className="flex min-h-0 flex-col">
                        <SectionTitle icon={FaChartBar} cls={sectionTitleCls}>
                          Transactions mensuelles par magasin (OK / échecs)
                        </SectionTitle>
                        <div className="h-72 min-h-[18rem] flex-1">
                          <Bar
                            data={{
                              labels: storesMonthlyChart.labels,
                              datasets: storesMonthlyChart.barDatasets,
                            }}
                            options={{
                              ...chartBase,
                              scales: {
                                ...chartBase.scales,
                                x: { ...chartBase.scales.x, stacked: true },
                                y: { ...chartBase.scales.y, stacked: true },
                              },
                            }}
                          />
                        </div>
                      </Card>
                    )}
                    {storesMonthlyChart.lineDatasets.length > 0 && (
                      <Card className="flex min-h-0 flex-col">
                        <SectionTitle icon={FaChartLine} cls={sectionTitleCls}>
                          Volume approuvé par magasin (TND)
                        </SectionTitle>
                        <div className="h-72 min-h-[18rem] flex-1">
                          <Line
                            data={{
                              labels: storesMonthlyChart.labels,
                              datasets: storesMonthlyChart.lineDatasets,
                            }}
                            options={{
                              ...chartBase,
                              plugins: {
                                ...chartBase.plugins,
                                tooltip: {
                                  ...chartBase.plugins.tooltip,
                                  callbacks: {
                                    label(ctx) {
                                      return `${ctx.dataset.label}: ${fmtTndDisplay(ctx.parsed.y)}`;
                                    },
                                  },
                                },
                              },
                              scales: {
                                ...chartBase.scales,
                                x: chartBase.scales.x,
                                y: { display: false },
                                y1: {
                                  type: 'linear',
                                  position: 'left',
                                  grid: { color: '#cbd5e1' },
                                  ticks: { color: '#475569', font: { size: 9 } },
                                },
                              },
                            }}
                          />
                        </div>
                      </Card>
                    )}
                  </div>
                )}
            </div>
          )}

          {/* ─── Incidents ─── */}
          {activeTab === 'incidents' && !tabLoading && !tabErrored && summary && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                <KpiCard
                  label="Total incidents"
                  value={fmtInt(totalNonApprovedIncidents)}
                  sub="Transactions non approuvées"
                  colorHex={DASH.accent2}
/>
                <KpiCard
                  label="TPE indisponible"
                  value={fmtInt(incFluxTuToCaRj.tu)}
                  sub={`${
                    totalNonApprovedIncidents > 0
                      ? fmtDec((incFluxTuToCaRj.tu / totalNonApprovedIncidents) * 100)
                      : '0'
                  } % des incidents`}
                  colorHex={DASH.purple}
/>
                <KpiCard
                  label="Rejetés"
                  value={fmtInt(incFluxTuToCaRj.rj)}
                  sub="Refus acquéreur / banque"
                  colorHex={DASH.red}
/>
                <KpiCard
                  label="Timeouts"
                  value={fmtInt(incFluxTuToCaRj.to)}
                  sub={`${
                    totalNonApprovedIncidents > 0
                      ? fmtDec((incFluxTuToCaRj.to / totalNonApprovedIncidents) * 100)
                      : '0'
                  } % des incidents`}
                  colorHex={DASH.orange}
/>
                <KpiCard
                  label="Annulés"
                  value={fmtInt(incFluxTuToCaRj.ca)}
                  sub="Annulations caissier"
                  colorHex={DASH.dim}
/>
                <KpiCard
                  label="Montant non encaissé (TND)"
                  value={
                    uncollectedTndInfo.amount != null ? fmtAmountTxnStyle(uncollectedTndInfo.amount) : '—'
                  }
                  sub={
                    uncollectedTndInfo.amount != null
                      ? uncollectedTndInfo.isEstimate
                        ? 'Estimation (Σ échecs × panier / mois)'
                        : 'Transactions non abouties'
                      : 'Donnée non disponible'
                  }
                  colorHex={DASH.pink}
/>
              </div>

              <Card className="flex min-h-0 flex-col">
                <SectionTitle icon={FaChartBar} cls={sectionTitleCls}>
                  Incidents par mois (TU / TO / CA / RJ)
                </SectionTitle>
                {incidentsSeries.length === 0 ? (
                  <p className={`text-xs ${subtitleClass}`}>Aucun incident sur la période.</p>
                ) : (
                  <div className="h-72 min-h-[18rem] flex-1">
                    <Bar
                      data={incidentsStackData}
                      options={{
                        ...chartBase,
                        scales: {
                          ...chartBase.scales,
                          x: { ...chartBase.scales.x, stacked: true },
                          y: { ...chartBase.scales.y, stacked: true },
                        },
                      }}
                    />
                  </div>
                )}
              </Card>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:items-stretch">
                <Card className="flex min-h-0 flex-col">
                  <SectionTitle icon={FaChartLine} cls={sectionTitleCls}>
                    Taux d'échec mensuel (%)
                  </SectionTitle>
                  {monthlyData.length === 0 ? (
                    <p className={`text-xs ${subtitleClass}`}>Aucune donnée mensuelle.</p>
                  ) : (
                    <div className="h-56 min-h-[14rem] flex-1">
                      <Line
                        data={{
                          labels: monthLabels,
                          datasets: [
                            {
                              label: "Taux d'échec (%)",
                              data: monthlyFailRates,
                              borderColor: DASH.red,
                              backgroundColor: 'rgba(239, 68, 68, 0.12)',
                              fill: true,
                              tension: 0.35,
                              pointRadius: 4,
                              pointBackgroundColor: monthlyFailRates.map((v) =>
                                v >= 25 ? DASH.red : v >= 15 ? DASH.orange : DASH.dim,
                              ),
                            },
                          ],
                        }}
                        options={{
                          ...chartBase,
                          plugins: { ...chartBase.plugins, legend: { display: false } },
                          scales: { ...chartBase.scales, y: { ...chartBase.scales.y, min: 0, max: 100 } },
                        }}
                      />
                    </div>
                  )}
                </Card>

                <Card className="flex min-h-0 flex-col">
                  <SectionTitle icon={FaChartBar} cls={sectionTitleCls}>
                    Codes retour les plus fréquents
                  </SectionTitle>
                  {topErrorCodesForChart.length === 0 ? (
                    <p className={`text-xs ${subtitleClass}`}>Aucun code retour sur cette période.</p>
                  ) : (
                    <div className="h-56 min-h-[14rem] flex-1">
                      <Bar
                        data={{
                          labels: topErrorCodesForChart.map((c) => String(c.code)),
                          datasets: [
                            {
                              label: 'Occurrences',
                              data: topErrorCodesForChart.map((c) => c.count ?? 0),
                              backgroundColor: topErrorBarColors,
                              borderRadius: 4,
                            },
                          ],
                        }}
                        options={{
                          indexAxis: 'y',
                          ...chartBase,
                          plugins: { ...chartBase.plugins, legend: { display: false } },
                        }}
                      />
                    </div>
                  )}
                </Card>
              </div>

              <Card>
                <SectionTitle icon={FaExclamationTriangle} cls={sectionTitleCls}>
                  Codes retour — détail (transactions non approuvées)
                </SectionTitle>
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className={`${tableHeadClass} font-semibold text-left`}>
                        <th className="py-1.5 px-2">Code</th>
                        <th className="py-1.5 px-2">Description</th>
                        <th className="py-1.5 px-2">Nb</th>
                        <th className="py-1.5 px-2">Sévérité</th>
                      </tr>
                    </thead>
                    <tbody>
                      {errCodeList.map((item, i) => {
                        const sev = getResponseCodeSeverityForDashboard(item.code);
                        return (
                          <tr key={`${item.code}-${i}`} className={`border-b ${borderClass}`}>
                            <td className={`py-1.5 px-2 font-dash-mono ${tableTextClass}`}>{item.code}</td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>
                              {getResponseCodeDescriptionForDashboard(item.code)}
                            </td>
                            <td className={`py-1.5 px-2 ${tableTextClass}`}>{fmtInt(item.count)}</td>
                            <td className="py-1.5 px-2">
                              <Badge status={sev === 'err' ? 'err' : sev === 'warn' ? 'warn' : 'info'}>
                                {severityLabel(sev)}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

        </main>
      </div>

      <div
        className={`pointer-events-none fixed bottom-3 right-4 z-20 text-[10px] ${subtitleClass}`}
        aria-hidden
      >
        Powered By Digicoser
      </div>
    </div>
  );
};

export default Dashboard;
